import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Alert from "@cloudscape-design/components/alert";
import Textarea from "@cloudscape-design/components/textarea";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { clientEditModalStore } from "../stores/clientEditModal";

class ClientEditModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			jsonInput: "",
			jsonError: "",
		};
	}

	validateAndFormatJson = (input) => {
		try {
			if (!input || input.trim() === "") {
				this.setState({ jsonError: "", jsonInput: "" });
				return { isValid: true, parsed: null };
			}

			const parsed = JSON.parse(input);
			if (!Array.isArray(parsed)) {
				this.setState({ jsonError: "Input must be a JSON array of numbers." });
				return { isValid: false };
			}

			if (!parsed.every(num => typeof num === "number" && !isNaN(num))) {
				this.setState({ jsonError: "All values in the array must be valid numbers." });
				return { isValid: false };
			}

			const formatted = JSON.stringify(parsed, null, 2);
			this.setState({ jsonError: "", jsonInput: formatted });
			return { isValid: true, parsed };
		} catch (error) {
			this.setState({ jsonError: "Invalid JSON format: " + error.message });
			return { isValid: false };
		}
	};

	componentDidMount(prevProps) {
		const { clientEditModal } = this.props;
		const initialJson = JSON.stringify(JSON.parse(clientEditModal.values.enabledProviders), null, 2);
		this.setState({ jsonInput: initialJson, jsonError: "" });
		this.validateAndFormatJson(initialJson);
	}

	handleJsonChange = (value) => {
		this.setState({ jsonInput: value });
	};

	editClient = async () => {
		const { clientEditModal, setClientEditModal, Interface } = this.props;


		if (!clientEditModal.id) {
			setClientEditModal({
				...clientEditModal,
				error: {
					text: "No client selected for editing.",
					code: 0,
					timestamp: Date.now(),
				},
			});
			return;
		}

		const { isValid, parsed } = this.validateAndFormatJson(this.state.jsonInput);
		if (!isValid && this.state.jsonInput.trim() !== "") {
			setClientEditModal({
				...clientEditModal,
				error: {
					text: this.state.jsonError,
					code: 0,
					timestamp: Date.now(),
				},
			});
			return;
		}

		setClientEditModal({ ...clientEditModal, loaded: false });

		try {

			const body = {
				id: clientEditModal.id,
				enabledProviders: parsed ?? clientEditModal.values.enabledProviders,
			};


			const result = await Interface.request(
				"set",
				{
					class: "data",
					module: "clients",
					method: "edit",
				},
				body
			);

			Interface.addNoty("Success", "Client was successfully edited");
			Interface.loadClients();
			setClientEditModal({
				...clientEditModal,
				show: false,
				loaded: true,
				id: 0,
				values: {
					enabledProviders: null,
				},
				error: { text: "", code: 0, timestamp: 0 },
			});
			this.setState({ jsonInput: "", jsonError: "" });
		} catch (error) {
			Interface.addNoty("Error", error.text || error.toString());
			setClientEditModal({
				...clientEditModal,
				loaded: true,
				error: {
					text: error.text || error.toString(),
					code: error.code || 500,
					timestamp: Date.now(),
				},
			});
		}
	};

	render() {
		const { clientEditModal, setClientEditModal } = this.props;
		const { jsonInput, jsonError } = this.state;

		return (
			<Modal
				visible={clientEditModal.show}
				size={"max"}
				onDismiss={() =>
					setClientEditModal({
						...clientEditModal,
						show: false,
						error: { text: "", code: 0, timestamp: 0 },
					})
				}
				header={`Edit Client (${clientEditModal.id})`}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								variant="link"
								onClick={() =>
									setClientEditModal({
										...clientEditModal,
										show: false,
										error: { text: "", code: 0, timestamp: 0 },
									})
								}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								onClick={this.editClient}
								loading={!clientEditModal.loaded}
								disabled={!clientEditModal.loaded}
							>
								Save Changes
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween size="m">
					{(clientEditModal.error.text || jsonError) && (
						<Alert type="error" children={clientEditModal.error.text || jsonError} />
					)}
					<Box variant="p">
						Edit the enabledProviders field as a JSON array of numbers for the selected client.
					</Box>
					<FormField
						label="Enabled Providers (JSON)"
						description="Enter a JSON array of numbers (e.g., [1, 2, 3]). Leave empty to keep current values."
						stretch
					>
						<Textarea
							value={jsonInput}
							onChange={({ detail }) => this.handleJsonChange(detail.value)}
							disabled={!clientEditModal.loaded}
							placeholder='[1, 2, 3]'
							rows={20}
						/>
					</FormField>
				</SpaceBetween>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const clientEditModal = useRecoilValue(clientEditModalStore);
		const setClientEditModal = useSetRecoilState(clientEditModalStore);

		return (
			<Component
				{...props}
				setClientEditModal={setClientEditModal}
				clientEditModal={clientEditModal}
			/>
		);
	};
}

export default withRecoil(ClientEditModal);
